import { useState, useCallback, useRef } from 'react';
import { authAxios } from '@/lib/axios';
import { parseMedia } from '@remotion/media-parser';
import { DEFAULT_UPLOAD_MIME_TYPES, isAcceptedMimeType } from '@/lib/upload/acceptedMimeTypes';

const MULTIPART_THRESHOLD = 100 * 1024 * 1024; // 100MB
const PART_SIZE = 100 * 1024 * 1024; // 100MB
const MAX_FILE_SIZE = 50 * 1024 * 1024 * 1024; // 50GB
const MAX_CONCURRENT_UPLOADS = 5;
const MAX_RETRIES = 3;

interface UploadUrlResponse {
  url: string;
  cdnUrl: string | null;
}

interface MultipartUploadResponse {
  uploadId: string;
  key: string;
  urls: { partNumber: number; url: string }[];
  cdnBaseUrl: string | null;
}

interface CompleteMultipartResponse {
  url: string;
}

export interface UploadProgress {
  percentage: number;
  uploadedBytes: number;
  totalBytes: number;
  uploadSpeed: number; // bytes per second
  remainingTime: number; // seconds
}

interface UseUploadFileReturn {
  getUploadUrl: (file: File, onProgress?: (progress: UploadProgress) => void, options?: { acceptedMimeTypes?: readonly string[] }) => Promise<string | null>;
  isLoading: boolean;
  error: string | null;
  createBlobUrl: (file: File) => string;
  revokeBlobUrl: (url: string) => void;
  getVideoDuration: (file: File) => Promise<number | null>;
  abortUpload: () => void;
}

interface PartSignal {
  aborted: boolean;
  xhr?: XMLHttpRequest;
}

function uploadPartXhr(
  url: string,
  blob: Blob,
  onProgress: (loaded: number) => void,
  signal: PartSignal,
): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal.aborted) {
      reject(new Error('Upload was aborted'));
      return;
    }

    const xhr = new XMLHttpRequest();
    signal.xhr = xhr;

    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable) {
        onProgress(event.loaded);
      }
    });

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error(`Part upload failed with status: ${xhr.status}`));
      }
    });

    xhr.addEventListener('error', () => {
      reject(new Error('Part upload failed due to network error'));
    });

    xhr.addEventListener('abort', () => {
      reject(new Error('Upload was aborted'));
    });

    xhr.open('PUT', url);
    xhr.send(blob);
  });
}

async function uploadPartWithRetry(
  url: string,
  blob: Blob,
  onProgress: (loaded: number) => void,
  signal: PartSignal,
): Promise<void> {
  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    try {
      await uploadPartXhr(url, blob, onProgress, signal);
      return;
    } catch (err: any) {
      if (signal.aborted || err.message === 'Upload was aborted') {
        throw err;
      }
      if (attempt === MAX_RETRIES) {
        throw err;
      }
      // Exponential backoff: 1s, 2s, 4s
      const delay = Math.pow(2, attempt) * 1000;
      await new Promise((r) => setTimeout(r, delay));
      onProgress(0); // Reset progress for this part on retry
    }
  }
}

export function useUploadFile(): UseUploadFileReturn {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const xhrRef = useRef<XMLHttpRequest | null>(null);
  const activeXhrsRef = useRef<Set<PartSignal>>(new Set());
  const multipartRef = useRef<{ uploadId: string; key: string } | null>(null);

  const simpleUpload = (
    file: File,
    onProgress?: (progress: UploadProgress) => void,
  ): Promise<string> => {
    return new Promise((resolve, reject) => {
      void (async () => {
        try {
          const response = await authAxios.post<UploadUrlResponse>('/api/get-upload-url', {
            contentType: file.type,
          });

          const { cdnUrl } = response.data;
          const parsedURL = new URL(response.data.url);

          const xhr = new XMLHttpRequest();
          xhrRef.current = xhr;
          const startTime = Date.now();
          let lastTime = startTime;
          let lastUploaded = 0;

          xhr.upload.addEventListener('progress', (event) => {
            if (event.lengthComputable && onProgress) {
              const now = Date.now();
              const timeSinceLastUpdate = (now - lastTime) / 1000;
              const bytesSinceLastUpdate = event.loaded - lastUploaded;
              const instantSpeed = timeSinceLastUpdate > 0 ? bytesSinceLastUpdate / timeSinceLastUpdate : 0;
              const remainingBytes = event.total - event.loaded;
              const remainingTime = instantSpeed > 0 ? remainingBytes / instantSpeed : 0;

              onProgress({
                percentage: (event.loaded / event.total) * 100,
                uploadedBytes: event.loaded,
                totalBytes: event.total,
                uploadSpeed: instantSpeed,
                remainingTime: Math.max(0, remainingTime),
              });

              lastTime = now;
              lastUploaded = event.loaded;
            }
          });

          xhr.addEventListener('load', () => {
            xhrRef.current = null;
            if (xhr.status >= 200 && xhr.status < 300) {
              resolve(cdnUrl || `${parsedURL.protocol}//${parsedURL.host}${parsedURL.pathname}`);
            } else {
              reject(new Error(`Upload failed with status: ${xhr.status}`));
            }
          });

          xhr.addEventListener('error', () => {
            xhrRef.current = null;
            reject(new Error('Upload failed due to network error'));
          });

          xhr.addEventListener('abort', () => {
            xhrRef.current = null;
            reject(new Error('Upload was aborted'));
          });

          xhr.open('PUT', response.data.url);
          xhr.send(file);
        } catch (err) {
          reject(err);
        }
      })();
    });
  };

  const multipartUpload = async (
    file: File,
    onProgress?: (progress: UploadProgress) => void,
  ): Promise<string> => {
    const numberOfParts = Math.ceil(file.size / PART_SIZE);

    const response = await authAxios.post<MultipartUploadResponse>(
      '/api/create-multipart-upload',
      { contentType: file.type, numberOfParts },
    );

    const { uploadId, key, urls } = response.data;
    multipartRef.current = { uploadId, key };

    const partProgress = new Map<number, number>();
    const startTime = Date.now();
    let lastTime = startTime;
    let lastTotalUploaded = 0;

    const reportProgress = () => {
      if (!onProgress) return;
      let totalUploaded = 0;
      partProgress.forEach((bytes) => { totalUploaded += bytes; });

      const now = Date.now();
      const timeSinceLastUpdate = (now - lastTime) / 1000;
      const bytesSinceLastUpdate = totalUploaded - lastTotalUploaded;
      const instantSpeed = timeSinceLastUpdate > 0 ? bytesSinceLastUpdate / timeSinceLastUpdate : 0;
      const remainingBytes = file.size - totalUploaded;
      const remainingTime = instantSpeed > 0 ? remainingBytes / instantSpeed : 0;

      onProgress({
        percentage: (totalUploaded / file.size) * 100,
        uploadedBytes: totalUploaded,
        totalBytes: file.size,
        uploadSpeed: instantSpeed,
        remainingTime: Math.max(0, remainingTime),
      });

      lastTime = now;
      lastTotalUploaded = totalUploaded;
    };

    // Upload parts with concurrency pool
    let nextIndex = 0;
    const abortedFlag = { aborted: false };

    const uploadNext = async (): Promise<void> => {
      while (nextIndex < urls.length) {
        if (abortedFlag.aborted) return;

        const idx = nextIndex++;
        const { partNumber, url } = urls[idx];
        const start = idx * PART_SIZE;
        const end = Math.min(start + PART_SIZE, file.size);
        const blob = file.slice(start, end);

        const signal: PartSignal = { aborted: false };
        activeXhrsRef.current.add(signal);

        try {
          await uploadPartWithRetry(
            url,
            blob,
            (loaded) => {
              partProgress.set(partNumber, loaded);
              reportProgress();
            },
            signal,
          );
          partProgress.set(partNumber, end - start);
          reportProgress();
        } finally {
          activeXhrsRef.current.delete(signal);
        }
      }
    };

    // Spawn concurrent workers
    const workers = Array.from(
      { length: Math.min(MAX_CONCURRENT_UPLOADS, urls.length) },
      () => uploadNext(),
    );
    await Promise.all(workers);

    // Complete the multipart upload — the backend retrieves ETags
    // via ListParts so we don't need to read them from the browser.
    const completeResponse = await authAxios.post<CompleteMultipartResponse>(
      '/api/complete-multipart-upload',
      { key, uploadId },
    );

    multipartRef.current = null;
    return completeResponse.data.url;
  };

  const getUploadUrl = async (
    file: File,
    onProgress?: (progress: UploadProgress) => void,
    options?: { acceptedMimeTypes?: readonly string[] },
  ): Promise<string | null> => {
    setIsLoading(true);
    setError(null);

    try {
      const accepted = options?.acceptedMimeTypes ?? DEFAULT_UPLOAD_MIME_TYPES;
      if (!isAcceptedMimeType(file.type, accepted)) {
        throw new Error(
          options?.acceptedMimeTypes
            ? 'Tipo de arquivo não suportado'
            : 'Apenas arquivos .mp4, .jpg, .png e .pdf são aceitos',
        );
      }

      if (file.size > MAX_FILE_SIZE) {
        throw new Error('O tamanho máximo permitido é 50GB');
      }

      if (file.size >= MULTIPART_THRESHOLD) {
        return await multipartUpload(file, onProgress);
      }
      return await simpleUpload(file, onProgress);
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Erro ao obter URL de upload';
      setError(errorMessage);
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  // Create blob URL for immediate preview
  const createBlobUrl = useCallback((file: File): string => {
    return URL.createObjectURL(file);
  }, []);

  // Revoke blob URL to free memory
  const revokeBlobUrl = useCallback((url: string): void => {
    URL.revokeObjectURL(url);
  }, []);

  // Get video duration using Remotion media parser
  const getVideoDuration = useCallback(async (file: File): Promise<number | null> => {
    try {
      const url = URL.createObjectURL(file);
      try {
        const { durationInSeconds } = await parseMedia({
          src: url,
          fields: { durationInSeconds: true },
        });
        return durationInSeconds || null;
      } finally {
        URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Error parsing video duration:', error);
      return null;
    }
  }, []);

  // Abort current upload
  const abortUpload = useCallback(() => {
    // Abort simple upload
    if (xhrRef.current) {
      xhrRef.current.abort();
      xhrRef.current = null;
    }

    // Abort all multipart XHRs
    activeXhrsRef.current.forEach((signal) => {
      signal.aborted = true;
      if (signal.xhr) {
        signal.xhr.abort();
      }
    });
    activeXhrsRef.current.clear();

    // Call server abort for multipart upload
    if (multipartRef.current) {
      const { uploadId, key } = multipartRef.current;
      multipartRef.current = null;
      authAxios.post('/api/abort-multipart-upload', { key, uploadId }).catch(() => {});
    }

    setIsLoading(false);
  }, []);

  return {
    getUploadUrl,
    isLoading,
    error,
    createBlobUrl,
    revokeBlobUrl,
    getVideoDuration,
    abortUpload,
  };
}
